"use client";

import { useEffect, useRef, useState, ReactNode, RefObject } from "react";

/**
 * LandingMaskOverlay - Globaler Mask-Cursor über die ganze Landingpage.
 *
 * Architektur (orientiert sich an minhpham.design):
 * - Ein einziger position:absolute Layer über die ganze Page
 * - Orange Hintergrund mit radial-gradient mask am Cursor
 * - Klein default (30px), groß (450px) über `.mask-collision-target` Elementen
 * - Reveal-Inhalt pro Section: wird absolute über die Section gerendert
 *   und nur durch die Cursor-Mask sichtbar
 *
 * Im Hero (kein registriertes Region) ist das Overlay ohne Reveal-Inhalt
 * sichtbar - der Hero hat seinen eigenen Mask-Effekt.
 */

export interface MaskRevealRegion {
  id: string;
  sectionRef: RefObject<HTMLElement>;
  // Reveal-Inhalt - wird absolute über die Section gerendert
  content: (ctx: { fadeOutProgress: number }) => ReactNode;
  // Optional: andere Hintergrundfarbe für diese Region
  bgColor?: string;
}

interface LandingMaskOverlayProps {
  regions: MaskRevealRegion[];
}

export function LandingMaskOverlay({ regions }: LandingMaskOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const cursorPosRef = useRef({ x: -9999, y: -9999 });
  const targetPosRef = useRef({ x: -9999, y: -9999 });
  const cursorSizeRef = useRef(0);
  const targetSizeRef = useRef(0);
  const animationRef = useRef<number | undefined>(undefined);
  const lastFrameTimeRef = useRef(0);

  // Page-Höhe für den Overlay-Container
  const [pageHeight, setPageHeight] = useState(0);

  // ---- Maus-Tracking + Lerp-Animation ----
  useEffect(() => {
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    const overlay = overlayRef.current;
    if (!overlay) return;

    // Größen-Konstanten (analog zu den Hero-Werten)
    const getSizes = () => {
      const w = window.innerWidth;
      const isMobile = w < 768;
      const isTablet = w >= 768 && w <= 1400;
      return {
        normalSize: isMobile ? 22 : isTablet ? 26 : 30,
        collisionSize: isMobile ? 220 : isTablet ? 360 : 450,
      };
    };

    const animate = (timestamp: number) => {
      if (!lastFrameTimeRef.current) lastFrameTimeRef.current = timestamp;
      const dt = Math.min((timestamp - lastFrameTimeRef.current) / 1000, 0.05);
      lastFrameTimeRef.current = timestamp;

      const LERP_SPEED = 8;
      const SIZE_LERP_SPEED = 8;

      // Position-Lerp
      const factor = 1 - Math.exp(-LERP_SPEED * dt);
      cursorPosRef.current.x +=
        (targetPosRef.current.x - cursorPosRef.current.x) * factor;
      cursorPosRef.current.y +=
        (targetPosRef.current.y - cursorPosRef.current.y) * factor;

      // Size-Lerp
      const sizeFactor = 1 - Math.exp(-SIZE_LERP_SPEED * dt);
      cursorSizeRef.current +=
        (targetSizeRef.current - cursorSizeRef.current) * sizeFactor;

      overlay.style.setProperty("--cx", `${cursorPosRef.current.x}px`);
      overlay.style.setProperty("--cy", `${cursorPosRef.current.y}px`);
      overlay.style.setProperty("--cs", `${cursorSizeRef.current}px`);

      animationRef.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      // Page-Koordinaten (clientX + scrollX, clientY + scrollY)
      const pageX = e.clientX + window.scrollX;
      const pageY = e.clientY + window.scrollY;
      targetPosRef.current.x = pageX;
      targetPosRef.current.y = pageY;

      // Erste Maus-Position: direkt setzen (kein Reinfliegen)
      if (cursorPosRef.current.x < -5000) {
        cursorPosRef.current.x = pageX;
        cursorPosRef.current.y = pageY;
      }

      // Collision-Detection: Cursor über Text-Element?
      const sizes = getSizes();
      const hovered = document.elementFromPoint(e.clientX, e.clientY);
      const overText = hovered?.closest(".mask-collision-target");
      targetSizeRef.current = overText ? sizes.collisionSize : sizes.normalSize;
    };

    const handleMouseLeaveDocument = () => {
      targetSizeRef.current = 0;
    };

    const handleMouseEnterDocument = (e: MouseEvent) => {
      const sizes = getSizes();
      const hovered = document.elementFromPoint(e.clientX, e.clientY);
      const overText = hovered?.closest(".mask-collision-target");
      targetSizeRef.current = overText ? sizes.collisionSize : sizes.normalSize;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.documentElement.addEventListener(
      "mouseleave",
      handleMouseLeaveDocument
    );
    document.documentElement.addEventListener(
      "mouseenter",
      handleMouseEnterDocument
    );

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.documentElement.removeEventListener(
        "mouseleave",
        handleMouseLeaveDocument
      );
      document.documentElement.removeEventListener(
        "mouseenter",
        handleMouseEnterDocument
      );
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // ---- Page-Höhe tracken ----
  useEffect(() => {
    const update = () => {
      setPageHeight(document.documentElement.scrollHeight);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(document.documentElement);
    window.addEventListener("resize", update);
    // Auch beim Scrollen (manche Sektionen ändern Höhe dynamisch)
    let rafId: number;
    const scrollLoop = () => {
      update();
      rafId = requestAnimationFrame(scrollLoop);
    };
    rafId = requestAnimationFrame(scrollLoop);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      ref={overlayRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: pageHeight ? `${pageHeight}px` : "100%",
        pointerEvents: "none",
        zIndex: 50,
        backgroundColor: "#F7931A",
        // Mask: kreisförmiger sichtbarer Bereich am Cursor
        // Innerhalb: schwarz (= sichtbar) - außerhalb: transparent (= unsichtbar)
        maskImage:
          "radial-gradient(circle at var(--cx, -9999px) var(--cy, -9999px), black calc(var(--cs, 0px) / 2 - 1px), transparent calc(var(--cs, 0px) / 2 + 1px))",
        WebkitMaskImage:
          "radial-gradient(circle at var(--cx, -9999px) var(--cy, -9999px), black calc(var(--cs, 0px) / 2 - 1px), transparent calc(var(--cs, 0px) / 2 + 1px))",
      }}
    >
      {regions.map((region) => (
        <RegionContent key={region.id} region={region} />
      ))}
    </div>
  );
}

// =====================================================
// RegionContent: Reveal-Inhalt einer Section, ko-positioniert
// =====================================================

function RegionContent({ region }: { region: MaskRevealRegion }) {
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    const update = () => {
      const el = region.sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      });
    };

    update();

    let rafId: number;
    const loop = () => {
      update();
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => cancelAnimationFrame(rafId);
  }, [region.sectionRef]);

  if (!pos) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        height: pos.height,
      }}
    >
      {region.content({ fadeOutProgress: 0 })}
    </div>
  );
}
